import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../../api/client';
import { useAdminAuth } from '../../../app/providers/useAdminAuth';
import { Button } from '../../../shared/ui/Button';
import { Field } from '../../../shared/ui/Field';
import { Input } from '../../../shared/ui/Input';
import { useMfaDisable } from '../hooks/useMfaDisable';
import styles from './DisableMfaModal.module.css';

interface DisableMfaModalProps {
  onClose: () => void;
}

// PARA-151, AC-6/AC-9/AC-E6: отключение своей MFA текущим TOTP-кодом.
// После 200 backend немедленно отзывает ВСЕ сессии аккаунта, включая текущую —
// явно вызываем logout() и уходим на /login (НЕ полагаемся на глобальный
// 401-handler). 400 — неверный код (остаёмся, повторный ввод), 429 — rate-limit.
export function DisableMfaModal({ onClose }: DisableMfaModalProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAdminAuth();
  const disable = useMfaDisable();
  const [totpCode, setTotpCode] = useState('');

  const submit = (): void => {
    disable.mutate(totpCode, {
      onSuccess: () => {
        logout();
        navigate('/login', { replace: true });
      },
    });
  };

  const disableStatus = disable.error instanceof ApiError ? disable.error.status : null;
  const disableErrorKey =
    disableStatus === 400
      ? 'admins.mfa.wrongCode'
      : disableStatus === 429
        ? 'admins.mfa.rateLimited'
        : 'admins.mfa.error';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="disable-mfa-title"
      className={styles.dialogOverlay}
    >
      <div className={styles.dialogContent}>
        <h3 id="disable-mfa-title">{t('admins.mfa.disableTitle')}</h3>
        <p className={styles.dialogField}>{t('admins.mfa.disableDescription')}</p>
        <Field
          label={t('admins.mfa.codeLabel')}
          htmlFor="disable-mfa-code"
          className={styles.dialogField}
        >
          <Input
            id="disable-mfa-code"
            value={totpCode}
            onChange={(event) => setTotpCode(event.target.value)}
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            autoComplete="one-time-code"
          />
        </Field>
        {disable.isError && (
          <p className={styles.error} role="alert">
            {t(disableErrorKey)}
          </p>
        )}
        <div className={styles.dialogActions}>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={disable.isPending}
            className={styles.dialogActionButton}
          >
            {t('admins.mfa.cancel')}
          </Button>
          <Button variant="primary" onClick={submit} disabled={disable.isPending}>
            {t('admins.mfa.disableSubmit')}
          </Button>
        </div>
      </div>
    </div>
  );
}
